import { walkDistance } from '../board/geometry';
import { strategicWinProbability as attackerWinProbability } from '../game/combatOdds';
import { applyBattleResult, applyMove, legalMoves } from '../game/game';
import { pieceAt, pieceById } from '../game/movement';
import type { FactionId, GameContext, GameState, Move } from '../game/types';
import { createRng } from '../rng/seededRandom';
import { withCardEvaluation } from '../game/cardPower';
import { candidateMoves, evaluate, type AiOptions } from './evaluate';
import { multiwayOutcomes, predictedWinnerDamage } from './fightOutcomes';
import { chooseMusterResponse } from './muster';

export {
  cardSearchOutcomes,
  fightWinners,
  multiwayOutcomes,
  predictedWinnerDamage,
  rollSearchOutcomes,
  type FightWinner,
} from './fightOutcomes';

export interface SearchOptions extends AiOptions {
  /** Plies to look ahead: 1 is the quick expected-value AI, 4 is slow and strong. */
  depth: number;
  /** Stop deepening after this many milliseconds; needs `now` (the core has no clock of its own). */
  timeLimitMs?: number;
  /**
   * Iterations deeper than `minDepth` are started only when they should end within this many
   * milliseconds of the start; the ones up to it may use the whole `timeLimitMs` (D-36).
   */
  softLimitMs?: number;
  minDepth?: number;
  now?: () => number;
  /** Replies considered per inner node after ordering (a beam keeps deep searches affordable). */
  beam?: number;
  /**
   * The beam by ply from the root: the first entry for the root's own moves (never cut), the second
   * for the replies to them, and the last for everything deeper. A fourth ply stays affordable by
   * narrowing there alone, so the shallower iterations are the same search as without it. `beam`
   * when omitted.
   */
  beams?: readonly number[];
  /**
   * Root moves searched at full depth in iterations of four plies or more; the rest are searched
   * a ply shallower and only re-searched at full depth when they beat the best so far. The
   * three-ply iteration, the fallback under a clock, is unchanged by it.
   */
  fullWidth?: number;
  /**
   * How much of the choice is chance, 0 … 1. Each root candidate's score is scaled to 0 … 1 across
   * the candidates and blended with a seeded draw: `(1 − randomness) · score + randomness · draw`.
   * At 0.5 a beginner's half-random game, where the best move often loses out to a fair one but
   * the worst never wins; at 0, the default, the exact best. The balance measurements never set it.
   */
  randomness?: number;
  /**
   * Variety in the opening (D-55): for each side's first `rounds` moves, every root move gets a
   * seeded bonus of 0 … `margin` evaluation units, and the move with the best score plus bonus is
   * played. It is never more than `margin` below the best move by this search's own judgement,
   * the same state always plays the same move, and another game's seed opens another way. Without
   * it a level with no chance opens every game alike, and those few lines decide who wins.
   */
  openingVariety?: { rounds: number; margin: number };
  /**
   * In the deepest iteration, after a fight at the horizon, let the other side recapture on the
   * same square before the position is judged, so a capture the search stops just short of is
   * seen through. Shallower iterations, the fallback under a clock, are unchanged by it.
   */
  extendCaptures?: boolean;
  /** Diagnostic only: exact scores for every root candidate; disables root-window pruning. */
  collectRootScores?: boolean;
}

export interface SearchResult {
  move: Move;
  /** From the mover's point of view, in evaluation units. */
  score: number;
  /** The deepest complete iteration. */
  depth: number;
  nodes: number;
  choices?: { move: Move; score: number }[];
}

/** The seed of a turn's chance: the game's own number, mixed with the turn and the side to move. */
export function chanceSeed(state: GameState): number {
  const side = state.factions.findIndex((f) => f.id === state.activeFaction);
  return (
    ((state.rng ^ 0x85ebca6b) +
      Math.imul(state.turn + 1, 0x9e3779b1) +
      Math.imul(side + 1, 0x27d4eb2f)) |
    0
  );
}

/** Scores are bounded so chance nodes can bound their children. */
const WIN = 10_000;
const BOUND = WIN + 1_000;
/**
 * How much longer each iteration is taken to run than the one before it, under a clock: the
 * growth last seen between two, within these bounds.
 */
const MIN_GROWTH = 2;
const MAX_GROWTH = 32;

/**
 * Alpha-beta search with iterative deepening over the board rules. The mover maximises, every
 * other faction minimises (paranoid for more than two players), and a capture is a chance node
 * weighted by the odds of the fight (Star1 bounds keep pruning sound through it). Deterministic
 * for a state and depth; ties at the root are broken with the game's RNG.
 */
export function searchMove(
  ctx: GameContext,
  state: GameState,
  options: SearchOptions,
): SearchResult {
  if (ctx.combatMode === 'card') ctx = withCardEvaluation(ctx);
  const me = state.activeFaction;
  const rng = createRng(state.rng ^ 0x9e3779b9);
  const candidates = candidateMoves(ctx, state);
  // Equal moves are tried in a seeded random order, so ties break differently game by game.
  const jitter = candidates.map(() => rng.next() * 0.5);
  const moves = orderMoves(ctx, state, candidates, me, jitter);
  if (moves.length === 0) return { move: { type: 'pass' }, score: 0, depth: 0, nodes: 0 };
  const randomness = Math.max(0, Math.min(1, options.randomness ?? 0));
  // A stream of their own, mixed with the turn, and drawn only when asked for, so a level without
  // chance keeps the jitter it had. From the state's number alone every turn between two fights
  // drew the same numbers, and a level with chance kept favouring the same slots (core-ai:bugs#9).
  const chance = randomness > 0 ? createRng(chanceSeed(state)) : null;
  const draws = moves.map(() => chance?.next() ?? 0);
  // Opening variety: each root move's bonus lowers the bar it has to clear, so the usual window
  // still prunes and only a move that could win with its bonus is searched exactly.
  const variety = options.openingVariety;
  const lean =
    variety && variety.margin > 0 && state.round < variety.rounds
      ? createRng(chanceSeed(state) ^ 0x2545f491)
      : null;
  const bonus = moves.map(() => (lean ? lean.next() * variety!.margin : 0));
  // Chance needs every root score exact, not the bound a cut-off subtree leaves behind.
  const exactRoot = options.collectRootScores || randomness > 0;
  const begun = options.now?.() ?? 0;
  const deadline =
    options.timeLimitMs !== undefined && options.now ? begun + options.timeLimitMs : Infinity;
  const softDeadline =
    options.softLimitMs !== undefined && deadline !== Infinity
      ? Math.min(deadline, begun + options.softLimitMs)
      : deadline;
  const search: Search = {
    ctx,
    me,
    evalOptions: {
      caution: options.caution ?? (options.depth > 1 ? 0.35 : 0.7),
      materialValues: options.materialValues,
      survivorModel: options.survivorModel,
      initiative: options.initiative,
    },
    counter: { nodes: 0 },
    timeUp: () => {
      if (search.aborted) return true;
      const up = options.now !== undefined && options.now() >= deadline;
      // An iteration past the first is thrown away once the clock runs out: stop it where it is.
      if (up && search.abortable) search.aborted = true;
      return up;
    },
    beams: options.beams?.length ? [...options.beams] : [options.beam ?? 24],
    extension: 0,
    abortable: false,
    aborted: false,
  };
  const fullWidth = options.fullWidth;

  let best = { move: moves[0]!, score: -Infinity, depth: 0 };
  let bestChoices: { move: Move; score: number }[] = [];
  let order = moves.map((_, i) => i);
  const deepest = Math.max(1, options.depth);
  const clock = deadline === Infinity ? undefined : options.now;
  let lastTook = 0;
  let growth: number | undefined;
  for (let depth = 1; depth <= deepest; depth++) {
    // Under a clock an iteration cut short is thrown away whole, so one that cannot finish is not
    // started: the move is the same, and the player does not wait out the clock for it. It is
    // judged by the last iteration grown as the one before grew, so from the third ply on.
    if (clock && growth !== undefined) {
      const until = depth > (options.minDepth ?? deepest) ? softDeadline : deadline;
      if (clock() + lastTook * growth > until) break;
    }
    const started = clock?.() ?? 0;
    search.extension = options.extendCaptures && depth === deepest ? 1 : 0;
    search.abortable = depth > 1;
    let alpha = -Infinity;
    let iterationBest: { index: number; score: number } | undefined;
    let complete = true;
    const iterationChoices: { move: Move; score: number }[] = [];
    const scores: number[] = [];
    for (const [rank, index] of order.entries()) {
      const move = moves[index]!;
      const child = applyMove(ctx, state, move);
      // Late root moves are tried a ply shallower; one that still beats the best gets the full look.
      const reduced = fullWidth !== undefined && depth >= 4 && rank >= fullWidth && !exactRoot;
      const bar = exactRoot ? -Infinity : alpha - bonus[index]!;
      let score = value(search, child, depth - (reduced ? 2 : 1), 1, bar, Infinity);
      if (reduced && score > bar && !search.timeUp())
        score = value(search, child, depth - 1, 1, bar, Infinity);
      if (search.timeUp() && depth > 1) {
        complete = false;
        break;
      }
      if (options.collectRootScores) iterationChoices.push({ move, score });
      scores[index] = score;
      // A value at or below the bar is only a bound (the subtree was cut off), never a better move.
      const judged = score + bonus[index]!;
      if (!iterationBest || judged > alpha) iterationBest = { index, score };
      alpha = Math.max(alpha, judged);
    }
    if (!complete || !iterationBest) break;
    if (clock) {
      const took = clock() - started;
      if (lastTook > 0) growth = Math.min(MAX_GROWTH, Math.max(MIN_GROWTH, took / lastTook));
      else if (depth > 1) growth = MIN_GROWTH;
      lastTook = took;
    }
    if (randomness > 0) iterationBest = blend(order, scores, draws, randomness);
    best = { move: moves[iterationBest.index]!, score: iterationBest.score, depth };
    bestChoices = iterationChoices;
    // Search the best move first next time round.
    order = [iterationBest.index, ...order.filter((i) => i !== iterationBest!.index)];
    if (Math.abs(best.score) >= WIN) break;
  }
  return {
    ...best,
    nodes: search.counter.nodes,
    ...(options.collectRootScores ? { choices: bestChoices } : {}),
  };
}

/**
 * The choice of a level that plays partly by chance (`SearchOptions.randomness`): each exact root
 * score scaled to 0 … 1 across the candidates, blended with that candidate's seeded draw.
 */
function blend(
  order: readonly number[],
  scores: readonly number[],
  draws: readonly number[],
  randomness: number,
): { index: number; score: number } {
  const known = order.map((i) => scores[i]!);
  const low = Math.min(...known);
  const span = Math.max(...known) - low;
  let best: { index: number; score: number; judged: number } | undefined;
  for (const index of order) {
    const scaled = span > 0 ? (scores[index]! - low) / span : 1;
    const judged = (1 - randomness) * scaled + randomness * draws[index]!;
    if (!best || judged > best.judged) best = { index, score: scores[index]!, judged };
  }
  return best!;
}

/** What every node of one search shares. */
interface Search {
  ctx: GameContext;
  me: FactionId;
  evalOptions: AiOptions;
  counter: { nodes: number };
  timeUp: () => boolean;
  /** `SearchOptions.beams`, always at least one entry. */
  beams: number[];
  /** Recapture plies the horizon may look through in this iteration (`extendCaptures`). */
  extension: number;
  /** Whether this iteration is thrown away if the clock runs out (every one but the first). */
  abortable: boolean;
  /** The clock ran out in an abortable iteration: every node returns at once. */
  aborted: boolean;
}

/** Replies kept at a node `ply` moves from the root (its replies are ply + 1). */
function beamAt(search: Search, ply: number): number {
  return search.beams[Math.min(ply + 1, search.beams.length) - 1]!;
}

function value(
  search: Search,
  state: GameState,
  depth: number,
  ply: number,
  alpha: number,
  beta: number,
  extension = search.extension,
): number {
  if (search.aborted) return 0;
  const { ctx, me, evalOptions } = search;
  search.counter.nodes += 1;
  // Every 64th node looks at the clock, leaf or not; past it, the iteration stops on the spot.
  if (search.abortable && (search.counter.nodes & 63) === 0 && search.timeUp()) return 0;
  // Muster belongs to the capture, not another board ply. Every side answers it as the AI seats
  // do (`chooseMusterResponse`) before the resulting fight is weighed, even at depth zero.
  while (state.phase === 'muster') state = applyMove(ctx, state, chooseMusterResponse(ctx, state));
  if (state.phase === 'ended') {
    // Prefer the quicker win and the later loss.
    const base = evaluate(ctx, state, me);
    return base > 0 ? base + depth : base < 0 ? base - depth : 0;
  }
  if (state.phase === 'battle' && state.pendingBattle) {
    return chance(search, state, depth, ply, alpha, beta, extension);
  }
  if (depth === 0) return evaluate(ctx, state, me, evalOptions);

  const mover = state.activeFaction;
  const maximising = mover === me;
  const moves = innerReplies(ctx, state, beamAt(search, ply));
  if (moves.length === 0) return evaluate(ctx, state, me, evalOptions);
  let best = maximising ? -Infinity : Infinity;
  for (const move of moves) {
    const child = applyMove(ctx, state, move);
    const score = value(search, child, depth - 1, ply + 1, alpha, beta);
    if (maximising) {
      best = Math.max(best, score);
      alpha = Math.max(alpha, score);
    } else {
      best = Math.min(best, score);
      beta = Math.min(beta, score);
    }
    if (alpha >= beta) break;
  }
  return best;
}

/**
 * The horizon after a fight: the side to move may leave the survivor on the square or recapture
 * it with its best few pieces, each another fight the chance node weighs, before the position is
 * judged. Only fights at the horizon pay for this, and only with a look at the legal moves.
 */
function recapture(
  search: Search,
  state: GameState,
  square: { x: number; y: number },
  alpha: number,
  beta: number,
  extension: number,
): number {
  if (search.aborted) return 0;
  const { ctx, me, evalOptions } = search;
  const standPat = evaluate(ctx, state, me, evalOptions);
  if (state.phase !== 'play') return standPat;
  const mover = state.activeFaction;
  const survivor = state.pieces.find((p) => p.x === square.x && p.y === square.y);
  if (!survivor || survivor.faction === mover) return standPat;
  const recaptures = legalMoves(ctx, state).filter(
    (move) => move.type === 'move' && move.x === square.x && move.y === square.y,
  );
  if (recaptures.length === 0) return standPat;
  const maximising = mover === me;
  if (maximising) alpha = Math.max(alpha, standPat);
  else beta = Math.min(beta, standPat);
  if (alpha >= beta) return standPat;
  let best = standPat;
  for (const move of orderMoves(ctx, state, recaptures, mover).slice(0, RECAPTURES)) {
    const score = value(search, applyMove(ctx, state, move), 0, 0, alpha, beta, extension);
    if (maximising) {
      best = Math.max(best, score);
      alpha = Math.max(alpha, score);
    } else {
      best = Math.min(best, score);
      beta = Math.min(beta, score);
    }
    if (alpha >= beta) break;
  }
  return best;
}

/** Recaptures the horizon looks through, best odds first. */
const RECAPTURES = 2;

/** A pending fight: the two outcomes weighted by the attacker's odds, with Star1 bounds. */
function chance(
  search: Search,
  state: GameState,
  depth: number,
  ply: number,
  alpha: number,
  beta: number,
  extension: number,
): number {
  const { ctx, evalOptions } = search;
  const battle = state.pendingBattle!;
  const p = attackerWinProbability(ctx, state);
  const attacker = pieceById(state, battle.attackerId)!;
  const defender = pieceById(state, battle.defenderId)!;
  const outcomes =
    (battle.participantIds?.length ?? 2) > 2
      ? multiwayOutcomes(ctx, state, evalOptions.survivorModel)
      : [
          {
            p,
            next: applyBattleResult(ctx, state, {
              winnerId: attacker.id,
              winnerDamage: predictedWinnerDamage(
                ctx,
                state,
                attacker,
                defender,
                evalOptions.survivorModel,
              ),
            }),
          },
          {
            p: 1 - p,
            next: applyBattleResult(ctx, state, {
              winnerId: defender.id,
              winnerDamage: predictedWinnerDamage(
                ctx,
                state,
                defender,
                attacker,
                evalOptions.survivorModel,
              ),
            }),
          },
          // Dropping tiny positive probabilities silently valued those outcomes at zero.
          // Keep their expected value; Star1 bounds handle safe pruning instead.
        ].filter((o) => o.p > 0);
  let total = 0;
  let remaining = 1;
  for (const outcome of outcomes) {
    // What the other outcomes could still contribute at best and at worst.
    const rest = remaining - outcome.p;
    const childAlpha = (alpha - total - rest * BOUND) / outcome.p;
    const childBeta = (beta - total + rest * BOUND) / outcome.p;
    const v =
      depth === 0 && extension > 0
        ? recapture(
            search,
            outcome.next,
            battle,
            Math.max(-BOUND, childAlpha),
            Math.min(BOUND, childBeta),
            extension - 1,
          )
        : value(
            search,
            outcome.next,
            depth,
            ply,
            Math.max(-BOUND, childAlpha),
            Math.min(BOUND, childBeta),
            extension,
          );
    total += outcome.p * v;
    remaining = rest;
    if (total + rest * BOUND <= alpha) return total + rest * BOUND;
    if (total - rest * BOUND >= beta) return total - rest * BOUND;
  }
  return total;
}

/**
 * The replies an inner node of the search weighs: the side to move's candidates, best first, cut
 * to the beam. Spells are ordered ahead of every quiet move, and a mage has dozens of them, so a
 * plain cut left the search a reply model of casts and captures in which no enemy ever simply
 * walked up to make a threat. Spells take at most a third of the beam while board moves are left
 * to fill the rest.
 */
export function innerReplies(ctx: GameContext, state: GameState, beam: number): Move[] {
  return keepBeam(orderMoves(ctx, state, candidateMoves(ctx, state, 4), state.activeFaction), beam);
}

function keepBeam(ordered: readonly Move[], beam: number): Move[] {
  if (ordered.length <= beam) return [...ordered];
  const spellRoom = Math.max(1, Math.floor(beam / 3));
  const kept: Move[] = [];
  const waiting: Move[] = [];
  let spells = 0;
  for (const move of ordered) {
    if (kept.length === beam) break;
    if (move.type === 'spell' && spells >= spellRoom) waiting.push(move);
    else {
      if (move.type === 'spell') spells++;
      kept.push(move);
    }
  }
  return [...kept, ...waiting].slice(0, beam);
}

/** Captures with good odds first, then spells, then moves that approach power points or enemies. */
function orderMoves(
  ctx: GameContext,
  state: GameState,
  moves: Move[],
  mover: FactionId,
  jitter?: number[],
): Move[] {
  const points = ctx.board.def.powerPoints.map(([x, y]) => ({ x, y }));
  const enemies = state.pieces.filter((p) => p.faction !== mover);
  const nearest = (x: number, y: number) => {
    const at = { x, y };
    let toPoint = 99;
    for (const q of points) toPoint = Math.min(toPoint, walkDistance(ctx.board, at, q));
    let toEnemy = 99;
    for (const q of enemies) toEnemy = Math.min(toEnemy, walkDistance(ctx.board, at, q));
    return Math.min(toPoint, toEnemy);
  };
  // Many moves are the same piece's: where it stands now is measured once.
  const before = new Map<number, number>();
  const key = (move: Move): number => {
    if (move.type === 'pass') return -1000;
    if (move.type === 'spell') return 50;
    if (move.type !== 'move') return move.type === 'join-battle' ? 25 : 0;
    const piece = pieceById(state, move.pieceId);
    if (!piece) return 0;
    const victim =
      pieceAt(state, move.x, move.y) &&
      state.pieces.find((p) => p.x === move.x && p.y === move.y && p.faction !== mover);
    if (victim) {
      const probe: GameState = {
        ...state,
        phase: 'battle',
        pendingBattle: { attackerId: piece.id, defenderId: victim.id, x: move.x, y: move.y },
      };
      const tier = ctx.roster[victim.character]?.tier ?? 1;
      return 100 + attackerWinProbability(ctx, probe) * tier * 20;
    }
    let from = before.get(piece.id);
    if (from === undefined) before.set(piece.id, (from = nearest(piece.x, piece.y)));
    return (from - nearest(move.x, move.y)) * 5;
  };
  return moves
    .map((move, i) => ({ move, i, k: key(move) + (jitter?.[i] ?? 0) }))
    .sort((a, b) => b.k - a.k || a.i - b.i)
    .map((m) => m.move);
}

/** Who is ahead, 0 … 1 for `faction`, from material and power points alone (no threat terms). */
export function advantage(ctx: GameContext, state: GameState, faction: FactionId): number {
  if (state.phase === 'ended')
    return state.winner === faction ? 1 : state.winner === 'draw' ? 0.5 : 0;
  const score = evaluate(ctx, state, faction, { caution: 0, balanceBar: true });
  return 0.5 + 0.5 * Math.tanh(score / 80);
}
